import { createApi } from '@reduxjs/toolkit/query/react';
import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { generateUrl } from 'utils/helpers';
import { isLogoutAction } from 'store/slices/auth';
import { providesList, cacheByIdArg, invalidatesList } from 'utils/query-cache';

import {
  State,
  StatesListResponse,
  CreateStatePayload,
  UpdateStatePayload,
  StateMutationResponse,
} from './types';

const statesAdapter = createEntityAdapter<State, number>({
  selectId: (item) => item.id,
});

const tagType = 'States';

export const statesApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiQuery,
  reducerPath: 'statesApi',
  endpoints: (build) => ({
    delete: build.mutation<State, number>({
      invalidatesTags: invalidatesList(tagType),
      query: (id) => ({
        method: 'delete',
        url: generateUrl(URL.DELETE_MASTER_STATE, { id }),
      }),
    }),

    list: build.query<State[], void>({
      providesTags: (result) => providesList(tagType)(result),
      transformResponse: (response: StatesListResponse) => response.data,
      query: () => ({
        method: 'get',
        url: URL.GET_STATES_MASTER_LIST,
      }),
    }),

    create: build.mutation<State, CreateStatePayload>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: StateMutationResponse) => response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.ADD_MASTER_STATE,
      }),
    }),

    update: build.mutation<State, UpdateStatePayload>({
      transformResponse: (response: StateMutationResponse) => response.data,
      invalidatesTags: (result, error, { state_id }) =>
        cacheByIdArg(tagType)(result, error, state_id),
      query: ({ name, state_id }) => ({
        data: { name },
        method: 'patch',
        url: generateUrl(URL.UPDATE_MASTER_STATE, { id: state_id }),
      }),
    }),
  }),
});

export const statesSlice = createSlice({
  reducers: {},
  name: 'states',
  initialState: statesAdapter.getInitialState(),
  extraReducers: (builder) => {
    builder.addMatcher(
      statesApi.endpoints.list.matchFulfilled,
      (state, { payload }) => {
        statesAdapter.setAll(state, payload);
      }
    );

    builder.addMatcher(
      statesApi.endpoints.create.matchFulfilled,
      (state, { payload }) => {
        statesAdapter.addOne(state, payload);
      }
    );

    builder.addMatcher(
      statesApi.endpoints.update.matchFulfilled,
      (state, { payload }) => {
        statesAdapter.updateOne(state, {
          changes: payload,
          id: statesAdapter.selectId(payload),
        });
      }
    );

    builder.addMatcher(isLogoutAction, (state) => {
      statesAdapter.removeAll(state);
    });
  },
});

export const {
  useListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
} = statesApi;
